<template>
    <material-card color="indigo" :title="$t('Pages.TravelWarrantSuggestion.submit_suggestion')">
        <v-layout wrap align-start justify-start>
            <v-flex lg3>
                <v-text-field v-debounce:300ms="refresh" slot="actions-prepend"
                              v-model="search.q"
                              append-icon="search"
                              :label="$t('Core.Fields.search')"
                              single-line
                              hide-details
                              color="purple"
                ></v-text-field>
            </v-flex>
            <v-flex lg3>
                <v-combobox
                        @change="refresh"
                        slot="actions-prepend"
                        v-model="search.status"
                        :label="$t('Pages.TravelWarrantSuggestion.model.status')"
                        :items="statuses"
                        single-line
                        hide-details
                        color="purple"
                ></v-combobox>
            </v-flex>
            <v-flex lg6>
                <v-btn round color="indigo" dark type="submit" @click="approveAll">
                    {{$t('Pages.TravelWarrantSuggestion.btn_submit')}}
                </v-btn>
                <v-btn round color="info" dark type="submit" @click="pair">
                    {{$t('Pages.TravelWarrantSuggestion.btn_pair')}}
                </v-btn>
                <v-btn round color="error" dark type="submit" @click="declineAll">
                    {{$t('Pages.TravelWarrantSuggestion.btn_decline')}}
                </v-btn>
            </v-flex>
            <v-flex lg3>
                <v-dialog
                        ref="dialog"
                        v-model="modal"
                        :return-value.sync="search.start"
                        persistent
                        lazy
                        full-width
                        width="290px"
                >
                    <v-text-field
                            slot="activator"
                            v-model="computedStart"
                            :label="$t('Pages.Vacations.model.start')"
                            prepend-icon="event"
                            readonly
                            color="indigo"
                            v-validate="'required'"
                            clearable
                            :error-messages="errors.first('start')"
                            name="start"
                            required
                    ></v-text-field>
                    <v-date-picker locale="sr-Latn-RS" first-day-of-week="1" color="indigo" v-model="search.start" scrollable>
                        <v-spacer></v-spacer>
                        <v-btn flat @click="modal = false">{{$t('Core.Fields.cancel')}}</v-btn>
                        <v-btn flat color="indigo" @click="$refs.dialog.save(search.start)">{{$t('Core.Fields.ok')}}</v-btn>
                    </v-date-picker>
                </v-dialog>
            </v-flex>
            <v-flex lg3>
                <v-dialog
                        ref="dialogEnd"
                        v-model="modalEnd"
                        :return-value.sync="search.end"
                        persistent
                        lazy
                        full-width
                        width="290px"
                >
                    <v-text-field
                            slot="activator"
                            v-model="computedEnd"
                            :label="$t('Pages.Vacations.model.end')"
                            prepend-icon="event"
                            readonly
                            color="indigo"
                            v-validate="'required'"
                            clearable
                            :error-messages="errors.first('end')"
                            name="end"
                            required
                    ></v-text-field>
                    <v-date-picker locale="sr-Latn-RS" first-day-of-week="1" color="indigo" v-model="search.end" scrollable>
                        <v-spacer></v-spacer>
                        <v-btn flat @click="modalEnd = false">{{$t('Core.Fields.cancel')}}</v-btn>
                        <v-btn flat color="indigo" @click="$refs.dialogEnd.save(search.end)">{{$t('Core.Fields.ok')}}</v-btn>
                    </v-date-picker>
                </v-dialog>
            </v-flex>
            <v-flex lg3>
                <v-autocomplete
                        :items="vehicles"
                        v-model="search.truck_registration"
                        v-validate="'required'"
                        :error-messages="errors.first('vehicle')"
                        :label="$t('Pages.Vehicles.title_singular')"
                        name="vehicle"
                        color="indigo"
                        clearable
                        autocomplete
                        item-text="licence_plates"
                        item-value="licence_plates"
                ></v-autocomplete>
            </v-flex>
        </v-layout>
        <v-data-table
                :headers="headers"
                :hide-actions="false"
                :items="items"
                :pagination.sync="pagination"
                :no-data-text="$t('Common.no_data_text')"
                :rows-per-page-items="perPageItems"
                select-all
                :total-items="total"
                item-key="id"
        >
            <template slot="headers" slot-scope="{headers}">
                <tr>
                    <th>
                        <v-checkbox
                                v-model="all"
                                :indeterminate="indeterminate"
                                primary
                                hide-details
                                @click.stop="toggleAll"
                        ></v-checkbox>
                    </th>
                    <th
                            v-for="header in headers"
                            :key="header.text"
                            :class="['column', header.sortable ? 'sortable' : '', pagination.descending ? 'desc' : 'asc', header.value === pagination.sortBy ? 'active' : '', header.align ? 'text-xs-' + header.align : 'text-xs-left']"
                            @click="changeSort(header.value, header.sortable)"
                    >
                        <span class="subheading font-weight-light text-primary" v-text="header.text"/>
                        <v-icon small v-if="header.sortable">arrow_upward</v-icon>
                    </th>
                </tr>
            </template>
            <template slot="items" slot-scope="{item}">
                <tr>
                    <td>
                        <v-checkbox
                                v-model="item.selected"
                                primary
                                hide-details
                                @click.stop="toggleSingle(item)"
                        ></v-checkbox>
                    </td>
                    <td>{{ item.id }}</td>
                    <td class="text-xs-center">{{ getLocaleDateFormat(item.date)}}</td>
                    <td class="text-xs-center">{{item.driver}}</td>
                    <td class="text-xs-center">{{item.truck_registration}}</td>
                    <td class="text-xs-center">{{ item.start }}</td>
                    <td class="text-xs-center">{{ item.end }}</td>
                    <td class="text-xs-center">{{ getWorkingHours(item) }}</td>
                    <td class="text-xs-center">{{$t('Pages.TravelWarrantSuggestion.status.' + item.status.toLowerCase())}}</td>
                </tr>
            </template>
        </v-data-table>
    </material-card>
</template>

<script>
    import i18n from '@/i18n'
    import _ from 'lodash'
    export default {
        name: "SelectableTable",
        components: {},
        props: {
            items: {
                type: Array,
                required: true
            },
            selected: {
                type: Array,
                required: true,
                default: []
            },
            total: {
                type: Number,
                default: 0,
            },
            loading: {
                type: Boolean,
                required: true
            },
            hideActions: {
                type: Boolean,
                default: false
            }
        },
        data: () => ({
            headers: [
                {
                    sortable: true,
                    text: '#',
                    value: 'id'
                },
                {
                    sortable: true,
                    text: i18n.t('Pages.TravelWarrantSuggestion.model.date'),
                    value: 'date',
                    align: 'center'
                },
                {
                    sortable: true,
                    text: i18n.t('Pages.TravelWarrantSuggestion.model.driver'),
                    value: 'driver',
                    align: 'center'
                },
                {
                    sortable: true,
                    text: i18n.t('Pages.TravelWarrantSuggestion.model.truck_registration'),
                    value: 'truck_registration',
                    align: 'center'
                },
                {
                    sortable: true,
                    text: i18n.t('Pages.TravelWarrantSuggestion.model.start'),
                    value: 'start',
                    align: 'center'
                },
                {
                    sortable: true,
                    text: i18n.t('Pages.TravelWarrantSuggestion.model.end'),
                    value: 'end',
                    align: 'center'
                },
                {
                    sortable: false,
                    text: i18n.t('Pages.TravelWarrantSuggestion.model.working_hours'),
                    align: 'center'
                },
                {
                    sortable: true,
                    text: i18n.t('Pages.TravelWarrantSuggestion.model.status'),
                    value: 'status',
                    align: 'center'
                },
            ],
            pagination: {sortBy: 'id', descending: true},
            all: false,
            search: {
                status: {},
                start: null,
                end: null,
                q: '',
            },
            vehicles: [],
            modal: false,
            modalEnd: false,
            perPageItems: [10, 15, 25, 50, 100],
            statuses: [],
            searchData:{},
        }),
        computed:{
            indeterminate() {
                return this.all ? false : ( (this.selected && this.selected.length >0  && this.selected.length !== this.items.length) ? true : false);
            },
            computedStart(val){
                return this.getLocaleDateFormat(this.search.start);
            },
            computedEnd(val){
                return this.getLocaleDateFormat(this.search.end);
            },
        },
        created(){
            this.statuses = [
                {
                    value: this.TravelWarrantSuggestionStatus.NEW,
                    text: this.$t('Pages.TravelWarrantSuggestion.status.new')
                },
                {
                    value: this.TravelWarrantSuggestionStatus.APPROVED,
                    text: this.$t('Pages.TravelWarrantSuggestion.status.approved')
                },
                {
                    value: this.TravelWarrantSuggestionStatus.DECLINED,
                    text: this.$t('Pages.TravelWarrantSuggestion.status.declined')
                },
            ];
            this.search.status = this.statuses[0];
            this.getVehicles();
        },
        mounted(){

        },
        methods: {

            refresh(){

                let data = {
                    q                   : this.search.q,
                    status              : this.search.status.value,
                    start               : this.search.start,
                    end                 : this.search.end,
                    truck_registration  : this.search.truck_registration,
                    page                : this.pagination.page,
                    pagination          : this.pagination.rowsPerPage,
                    orderBy             : this.pagination.sortBy ? (this.pagination.sortBy + "," + (this.pagination.descending ? 'desc' : 'asc')) : null
                };

                this.$emit('refresh', data);
                this.all = true;
                this.toggleAll();
            },
            toggleAll () {
                this.all = !this.all;
                for(var i = 0; i < this.items.length; i++) {
                    let item = this.items[i];
                    if(this.all) {
                        if(!item.selected) {
                            this.selected.push(item);
                        }
                    }
                    else{
                        if(item.selected) {
                            _.remove(this.selected, {
                                id: item.id
                            });
                        }
                    }

                }

                this.$emit('select', this.selected);
            },
            toggleSingle(item){
                if(item.selected) {
                    _.remove(this.selected, {
                        id: item.id
                    });
                }
                else{
                    this.selected.push(item);
                }
                this.all = this.selected.length === this.items.length;
                this.select(this.selected);
            },
            changeSort (column, sortable) {
                if(!sortable)return;

                if (this.pagination.sortBy === column) {
                    this.pagination.descending = !this.pagination.descending;
                } else {
                    this.pagination.sortBy = column;
                    this.pagination.descending = false;
                }

                this.refresh();
            },
            select(selected){
                this.$emit('select', selected);
            },
            async approveAll(){
                await this.$emit('approveAll', this.selected);
            },
            async pair(){
                await this.$emit('pair', this.selected);
            },
            async declineAll(){
                await this.$emit('declineAll', this.selected);
            }
        },
        watch: {
            'search.status': function(){
                this.refresh();
            },
            'search.truck_registration': function(){
                this.refresh();
            },
            'search.start': function(){
                this.refresh();
            },
            'search.end': function(){
                this.refresh();
            },
            items(val){
                //this.checkAll();
            },
            selected(val){
                //console.log(val);
            },
            pagination(val){
                this.refresh();
            },
        }
    }
</script>

<style scoped>

</style>
